import { useEffect, useRef, useState } from "react";
import Style from "./style.module.css";
import {
  ActivitySquareIcon,
  ChevronDownIcon,
  HistoryIcon,
  LogInIcon,
  PlusIcon,
} from "lucide-react";
import { Alert, Skeleton, Snackbar } from "@mui/material";
import { useApiCall } from "../../../../../../component/custom-hooks/api-call";
import DataNotFound from "../../../../../../component/ui/loader/data-not-found";
import * as XLSX from "xlsx";
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableFilterReturn,
  TableHead,
  TableHeaderWrap,
  TableRow,
} from "../../../../../../component/table/table-layout";
import DynamicFilter from "../../../../../../component/table/filter-table/dynamic-filter";
import CustomizeTabsPopup from "../../../../../../component/table/customize-table";
import LayoutTopIcon from "../../../../../../component/icons/layout-top";
import FilterFunnel01Icon from "../../../../../../component/icons/filter-funnel-01";
import Tooltip from "../../../../../../component/ui/tooltip";
import Swal from "sweetalert2";
import FileCode1Icon from "../../../../../../component/icons/file-code-1";
import { CustomDropdownMenuList } from "../../../../../../component/ui/dropdown/CustomDropdownMenuList";
import { CustomDropdownMenu } from "../../../../../../component/ui/dropdown/CustomDropdownMenu";
import { CustomDropdownButton } from "../../../../../../component/ui/dropdown/CustomDropdownButton";
import DotsVerticalIcon from "../../../../../../component/icons/dots-vertical";
import { CustomDropdown } from "../../../../../../component/ui/dropdown/CustomDropdown";
import NewEditIcon from "../../../../../../component/icons/action-edit-icon";
import StatusChip from "../../../../../../component/ui/chip/status";
import InnerLoader from "../../../../../../component/ui/loader/inner-loader";
import AddAccountManager from "../add-account-manager";
import AvatarProfile from "../../../../../../component/ui/profile";

const SettingAccountManager = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [apiData, setApiData] = useState([]);
  const [showModalFilterTable, setShowModalFilterTable] = useState(false);
  const [filterReturn, setFilterReturn] = useState([]);
  const [saveFilter, setSaveFilter] = useState([]);
  const [tablePagination, setTablePagination] = useState({});
  const [btnLoader, setBtnLoader] = useState(false);
  const [alertData, setAlertData] = useState({ isShow: false });

  const [showModalCustomizeTabs, setShowModalCustomizeTabs] = useState(false);
  const [tableHeader, setTableHeader] = useState([]);

  const [currentData, setCurrentData] = useState({});
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [pageItemsCount, setPageItemsCount] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [brands, setBrands] = useState([]);
  const [exportTo, setExportTo] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [tierList, setTierList] = useState([]);

  const handleClose = () => {
    setAlertData({ isShow: false });
  };

  const apiCall = useApiCall();

  useEffect(() => {
    getData({});
    getTeams();
    getUsers();
    getBrands();
    getTiers();
  }, []);

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedAgents(apiData.map((d) => d.userid));
    } else {
      setSelectedAgents([]);
    }
  };

  // ✅ Toggle Single Row
  const toggleSelectRow = (userid) => {
    setSelectedAgents((prev) =>
      prev.includes(userid)
        ? prev.filter((id) => id !== userid)
        : [...prev, userid],
    );
  };

  const handleExport = async (type, localData = null) => {
    try {
      // ✅ If user already selected rows (local data)
      if (localData && localData.length > 0) {
        exportData(localData, type);
        return;
      }

      const filters = filterReturn || [];
      const page = 1;
      const page_quantity = "all";

      const data = { page, page_quantity, filters };

      Swal.fire({
        title: "Exporting Agents...",
        text: "Please wait while we export your data.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // 🧩 Convert filters array → object
      const filtersObj = data.filters.reduce((acc, f) => {
        if (f.value !== "" && f.value !== null && f.value !== undefined) {
          acc[f.field] = f.value;
        }
        return acc;
      }, {});

      const transformed = {
        page_quantity: String(data.page_quantity || ""),
        page: String(data.page || "1"),
        ...filtersObj,
      };

      const apiData = JSON.stringify(transformed);

      const res = await apiCall({
        url: `users/agents-list-settings?usertype=2`,
        method: "get",
        data: apiData,
      });

      const agentsData = res?.data || res?.agents || [];
      if (!agentsData?.length) {
        Swal.fire({
          title: "Warning!",
          text: "No agents data available to export.",
          icon: "warning",
          confirmButtonText: "OK",
          buttonsStyling: false,
          customClass: { confirmButton: Style.customSwalBtn },
        });
        return;
      }

      exportData(agentsData, type);
    } catch (error) {
      console.error("Export Error:", error);
      Swal.fire({
        title: "Error!",
        text: "Something went wrong while exporting.",
        icon: "error",
        confirmButtonText: "OK",
        buttonsStyling: false,
        customClass: { confirmButton: Style.customSwalBtn },
      });
    }
  };

  const exportData = (data, type) => {
    const visibleColumns = tableHeader
      ?.filter((col) => col.is_visible === 1 && col.show == 1)
      ?.map((col) => ({ key: col.key, label: col.value }));

    const formattedData = data.map((row) => {
      const obj = {};

      obj["Agent Name"] = row?.name || "-";
      obj["Agent Email"] = row?.email || "-";
      obj["Agent Phone"] = row?.phone || "-";

      visibleColumns.forEach(({ key, label }) => {
        if (["name", "email", "phone"].includes(key)) return; // skip duplicates

        if (key === "usertype") {
          obj[label || "User Type"] = row?.usertype || "-";
        } else if (key === "country") {
          obj[label || "Country"] = row?.country || "-";
        } else if (key === "state") {
          obj[label || "State"] = row?.state || "-";
        } else if (key === "city") {
          obj[label || "City"] = row?.city || "-";
        } else if (key === "team_info") {
          obj[label || "Team Name"] = row?.team_info?.team_name || "-";
        } else {
          obj[label || key] =
            typeof row[key] === "object" && row[key] !== null
              ? JSON.stringify(row[key])
              : (row[key] ?? "-");
        }
      });

      return obj;
    });

    // 📁 File name
    const fileName = `agents${new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:T]/g, "-")}`;

    // 💾 Export logic
    if (type === "csv") {
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const csvContent = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}.csv`;
      link.click();
    } else if (type === "excel") {
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Agents");
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    }

    Swal.fire({
      title: "Success!",
      text: "Agents data exported successfully.",
      icon: "success",
      confirmButtonText: "OK",
      buttonsStyling: false,
      customClass: { confirmButton: Style.customSwalBtn },
    });
  };

  const getData = async ({
    filter = filterReturn,
    page,
    page_quantity = pageItemsCount,
  }) => {
    if (!page) {
      setIsLoading(true);
    }
    let extraUrl = `&page=${page || 1}&page_quantity=${page_quantity}`;
    for (let i = 0; i < filter?.length; i++) {
      extraUrl += `&${filter[i]?.name}=${filter[i]?.value}`;
    }

    try {
      const res = await apiCall({
        method: "get",
        url: `users/agents-list-settings?usertype=2${extraUrl}`,
      });
      if (page) {
        setApiData((prev) => [...prev, ...res.data]);
      } else {
        setApiData(res.data);
      }

      setTableHeader(res?.applied_headers);
      setSaveFilter(res?.custom_filters);
      setTablePagination(res?.pagination);
      setBtnLoader(false);

      setIsLoading(false);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  const getTeams = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: `teams/teams-list`,
      });
      setTeams(res?.data);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };
  const getBrands = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: `brands/brands-list`,
      });
      setBrands(res?.data);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };
  const getUsers = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: `users/user-type-list`,
      });
      setUsers(res?.data);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  const getTiers = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: `am-config/am-tiers-list`,
      });
      console.log("Tier list", res?.data);

      setTierList(res?.data);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  const onSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message });
    setShowModal(false);
    getData({});
  };

  const onError = (message) => {
    setAlertData({ isShow: true, type: "error", message: message });
  };

  const editAgent = (data) => {
    setCurrentData(data);
    setShowModal(true);

    console.log("agent Data", data);
  };

  const filterModalData = [
    {
      name: "name",
      label: "Name",
      options: ["is", "contains"],
      selectedOpt: "is",
      value: "",
      type: "text",
    },
    {
      name: "email",
      label: "Email",
      options: ["is", "contains"],
      selectedOpt: "is",
      value: "",
      type: "text",
    },
    {
      name: "team_id",
      label: "Team",
      options: ["is"],
      selectedOpt: "is",
      value: "",
      type: "dropdown",
      dropdown: teams,
      dropdownKey: "team_name",
      dropdownVal: "id",
    },
    {
      name: "usertype",
      label: "User Type",
      options: ["is"],
      selectedOpt: "is",
      value: "",
      type: "dropdown",
      dropdown: users,
      dropdownKey: "usertype",
      dropdownVal: "usertypeid",
    },
  ];

  const handleLoginAsAgent = async (agentId) => {
    try {
      const response = await apiCall({
        method: "post",
        url: `login-as-agent/${agentId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const token = response.access_token;
      const agentData = response.data;

      // Open new tab for agent
      const agentWindow = window.open("/", "_blank"); // same app
      agentWindow.onload = () => {
        // Store token in THAT tab's sessionStorage (not shared)
        agentWindow.sessionStorage.setItem("agent_token", token);
        agentWindow.sessionStorage.setItem(
          "agent_user",
          JSON.stringify(agentData),
        );
      };
    } catch (error) {
      console.error("Login as Agent failed:", error);
      alert(error.response?.data?.error || "Failed to login as agent");
    }
  };

  // const [expandedRows, setExpandedRows] = useState([]);

  // const toggleRow = (id) => {
  //   setExpandedRows((prev) =>
  //     prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
  //   );
  // };

  const [sessionData, setSessionData] = useState({});
  const [sessionLoading, setSessionLoading] = useState(false);

  const getSession = async (id) => {
    setSessionLoading(true);
    try {
      const res = await apiCall({
        method: "get",
        url: `users/agents-sessions/${id}`,
      });
      console.log("res SESSION API :", res?.data);

      setSessionData(res?.data);
    } catch (error) {
      console.warn("Error : ", error);
    } finally {
      setSessionLoading(false);
    }
  };

  const [openRow, setOpenRow] = useState("");
  const toggleRow = (id) => {
    if (id == openRow) {
      setOpenRow("");
      setSessionData([]);
      return;
    }
    setOpenRow(id);
    getSession(id);
  };

  const handleLogout = async (tokenId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to logout this device?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, logout",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const res = await apiCall({
          method: "post",
          url: `logout-specific/${tokenId}`,
        });
        if (res.status === 200) {
          Swal.fire({
            icon: "success",
            title: "Logged out!",
            text: "The device has been logged out successfully.",
          });

          getData({});
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.res?.data?.message || "Something went wrong.",
        });
      }
    }
  };

  const tableRef = useRef(null);

  return (
    <>
      <div className={`${Style.mainBody} `}>
        <div className={Style.teamSpaceDashboard}>
          <div className={Style.teamSpaceDashboard_main}>
            <div className={Style.teamSpaceDashboard_body}>
              <TableFilterReturn
                filterReturn={filterReturn}
                onChange={(allFilter) => {
                  setFilterReturn(allFilter);
                  getData({ filter: allFilter });
                }}
              />
              {/* HEADER */}
              <div className={Style.empDirectory_header}>
                <div className={Style.empDirectory_header_left}>
                  <div className={Style.bpfl_select}>
                    <select
                      onChange={(e) => {
                        getData({ page_quantity: e?.target.value });
                        setPageCount(1);
                        setPageItemsCount(e?.target.value);
                      }}
                    >
                      <option value="10">AM View 10</option>
                      <option value="25">AM View 25</option>
                      <option value="50">AM View 50</option>
                      <option value="100">AM View 100</option>
                      <option value="all">AM View all</option>
                    </select>
                  </div>
                </div>

                <div className={Style.empDirectory_header_right}>
                  <div className={Style.bpfl_action}>
                    <div
                      className={Style.bpfl_action}
                      onClick={() => {
                        setShowModal(true);
                      }}
                    >
                      <Tooltip
                        content={"Add Account Manager"}
                        position={"left center"}
                        className={`${Style.bpfl_actionBtn} ${Style.addBtn}`}
                      >
                        <div className={Style.icon}>
                          <PlusIcon size="1em" />
                        </div>
                      </Tooltip>
                    </div>

                    <Tooltip
                      className={Style.bpfl_actionItems}
                      content={"Table Filter"}
                      position={"left center"}
                    >
                      <div
                        className={`${Style.bpfl_actionBtn} ${
                          filterReturn?.length ? Style.active : null
                        }`}
                        onClick={() => {
                          setShowModalFilterTable(true);
                        }}
                      >
                        <div className={Style.icon}>
                          <FilterFunnel01Icon />
                        </div>
                      </div>
                    </Tooltip>

                    <Tooltip
                      className={`${Style.bpfl_actionItems}`}
                      content={"Customize Table"}
                      position={"left center"}
                    >
                      <div
                        className={`${Style.bpfl_actionBtn}`}
                        onClick={() => {
                          setShowModalCustomizeTabs(true);
                        }}
                      >
                        <div className={Style.icon}>
                          <LayoutTopIcon />
                        </div>
                      </div>
                    </Tooltip>

                    <CustomDropdown
                      position="bottom-right"
                      className={`myDropdown`}
                    >
                      <CustomDropdownButton arrow={false} className="btnClass">
                        <DotsVerticalIcon />
                      </CustomDropdownButton>

                      <CustomDropdownMenu>
                        <CustomDropdownMenuList
                          className={Style.CustomDropdownMenuList}
                          closed={false}
                        >
                          <div
                            className={Style.CustomDropdownMenuListBtn}
                            onClick={() => setExportTo(!exportTo)}
                          >
                            <div className="">
                              <LayoutTopIcon /> Export to
                            </div>
                            <div
                              className={`${Style.dropArrow} ${
                                exportTo ? Style.isActive : null
                              }`}
                            >
                              <ChevronDownIcon />
                            </div>
                          </div>

                          {exportTo ? (
                            <div className={Style.subItemMenu}>
                              <CustomDropdownMenuList
                                className={Style.subItemList}
                                onClick={() => {
                                  const dataToExport =
                                    selectedAgents.length > 0
                                      ? apiData.filter((d) =>
                                          selectedAgents.includes(d.userid),
                                        )
                                      : apiData;

                                  // ✅ Call the new unified export function
                                  handleExport("csv", dataToExport);
                                }}
                              >
                                <div className="d-flex align-items-center gap-1">
                                  <FileCode1Icon /> CSV
                                </div>
                              </CustomDropdownMenuList>

                              <CustomDropdownMenuList
                                className={Style.subItemList}
                                onClick={() => {
                                  const dataToExport =
                                    selectedAgents.length > 0
                                      ? apiData.filter((d) =>
                                          selectedAgents.includes(d.userid),
                                        )
                                      : apiData;

                                  // ✅ Call the new unified export function
                                  handleExport("excel", dataToExport);
                                }}
                              >
                                <div className="d-flex align-items-center gap-1">
                                  <FileCode1Icon /> XLSX
                                </div>
                              </CustomDropdownMenuList>
                            </div>
                          ) : null}
                        </CustomDropdownMenuList>
                      </CustomDropdownMenu>
                    </CustomDropdown>
                  </div>
                </div>
              </div>

              {/* BODY */}
              <div className={Style.empDirectory_body}>
                {isLoading ? (
                  <InnerLoader />
                ) : !apiData?.length ? (
                  <div className="flex justify-center items-center w-full h-full">
                    <DataNotFound title={"Account Manager"} />
                  </div>
                ) : (
                  <>
                    <TableCard ref={tableRef}>
                      <Table>
                        <TableHead>
                          <TableRow className="!z-40">
                            <TableCell header sticky className="!z-30">
                              <div className="flex gap-2 items-center">
                                <div className={Style.tableCheckBox}>
                                  <label className={Style.custom_checkbox}>
                                    <input
                                      type="checkbox"
                                      checked={
                                        selectedAgents.length ===
                                          apiData.length && apiData.length > 0
                                      }
                                      className={Style.customCheckbox}
                                      onChange={(e) =>
                                        toggleSelectAll(e.target.checked)
                                      }
                                    />
                                    <span className={Style.checkmark}></span>
                                  </label>
                                </div>
                                <TableHeaderWrap>
                                  <span className="pl-[50px]">Name</span>
                                </TableHeaderWrap>
                              </div>
                            </TableCell>

                            {tableHeader?.map((eachHead, index) =>
                              eachHead.is_visible === 1 &&
                              eachHead?.show == 1 ? (
                                <TableCell header key={index} className="!z-20">
                                  <TableHeaderWrap>
                                    <span>{eachHead?.value}</span>
                                  </TableHeaderWrap>
                                </TableCell>
                              ) : null,
                            )}

                            <TableCell header sticky className="!z-20">
                              <TableHeaderWrap>
                                <span className="pl-[50px]">Sessions</span>
                              </TableHeaderWrap>
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {apiData?.map((eachList, index) => {
                            return (
                              <>
                                <TableRow key={`${index}`}>
                                  <TableCell sticky>
                                    <div
                                      className={`${Style.tableActionArea} !gap-4`}
                                    >
                                      <div className="flex gap-2 items-center">
                                        <div className={Style.tableCheckBox}>
                                          <label
                                            className={Style.custom_checkbox}
                                          >
                                            <input
                                              type="checkbox"
                                              className={Style.customCheckbox}
                                              checked={selectedAgents.includes(
                                                eachList?.userid,
                                              )}
                                              onChange={() =>
                                                toggleSelectRow(
                                                  eachList?.userid,
                                                )
                                              }
                                            />
                                            <span
                                              className={Style.checkmark}
                                            ></span>
                                          </label>
                                        </div>

                                        {/* <div className="flex items-center gap-3">
                                          <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 grid place-items-center text-xs font-medium object-cover object-center">
                                            {eachList?.userimg ? (
                                              <img
                                                className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 grid place-items-center text-xs font-medium object-cover object-center"
                                                src={eachList?.userimg}
                                                alt={eachList?.name}
                                                onError={(e) => {
                                                  e.target.classList.add(
                                                    "hidden"
                                                  );
                                                  if (e.target.parentElement) {
                                                    e.target.parentElement.innerHTML = `
                              <div class="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 grid place-items-center text-xs font-medium">
                                ${eachList?.name?.split(" ")[0]?.[0] || ""}${eachList?.name?.split(
                                                      " "
                                                    )[1]?.[0] || ""
                                                      }
                              </div>`;
                                                  }
                                                }}
                                              />
                                            ) : (
                                              <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 grid place-items-center text-xs font-medium">
                                                {eachList?.name?.split(" ")[0]
                                                  ? eachList?.name?.split(
                                                    " "
                                                  )[0][0]
                                                  : ""}
                                                {eachList?.name?.split(" ")[1]
                                                  ? eachList?.name?.split(
                                                    " "
                                                  )[1][0]
                                                  : ""}
                                              </div>
                                            )}
                                          </div>

                                          <div className="font-medium leading-5">
                                            {eachList?.name}
                                          </div>
                                        </div> */}
                                        <AvatarProfile
                                          src={eachList?.userimg}
                                          title={eachList?.name}
                                        />
                                      </div>
                                      <div
                                        className={
                                          Style.tableActionAreaBtnGrpup
                                        }
                                      >
                                        <div
                                          className={
                                            "w-7 h-7 flex items-center justify-center rounded-md bg-[rgba()] shadow-[0_4px_12px_rgba(0,0,0,0.08)] border border-gray-100 hover:scale-105 transition-transform duration-300"
                                          }
                                          onClick={() => {
                                            editAgent(eachList);
                                          }}
                                        >
                                          <div
                                            className={Style.icon}
                                            style={{ color: "#FF9500" }}
                                          >
                                            <NewEditIcon className="h-4.5 w-4.5" />
                                          </div>
                                        </div>

                                        <div
                                          className={
                                            "w-7 h-7 flex items-center justify-center rounded-md bg-[rgba()] shadow-[0_4px_12px_rgba(0,0,0,0.08)] border border-gray-100 hover:scale-105 transition-transform duration-300"
                                          }
                                          onClick={() =>
                                            handleLoginAsAgent(eachList?.userid)
                                          }
                                        >
                                          <div
                                            className={Style.icon}
                                            style={{ color: "#FF9500" }}
                                          >
                                            <LogInIcon className="h-3.5 w-3.5" />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </TableCell>
                                  {tableHeader?.map((eachHead, i) =>
                                    eachHead.is_visible === 1 &&
                                    eachHead?.show == 1 ? (
                                      <>
                                        {eachHead.key === "team_info" ? (
                                          <TableCell key={`${i}-${index}`}>
                                            {eachList?.team_info ? (
                                              <div className="flex gap-1">
                                                <span>
                                                  {
                                                    eachList?.team_info
                                                      ?.team_name
                                                  }
                                                </span>
                                                .
                                                <span>
                                                  {
                                                    eachList?.team_info
                                                      ?.team_type
                                                  }
                                                </span>
                                              </div>
                                            ) : (
                                              "--"
                                            )}
                                          </TableCell>
                                        ) : eachHead.key == "email" ? (
                                          <TableCell key={`td-${i}`}>
                                            {eachList[eachHead?.key] ? (
                                              <a
                                                href={
                                                  `mailto:${
                                                    eachList[eachHead?.key]
                                                  }` || ""
                                                }
                                              >
                                                {eachList[eachHead?.key] ||
                                                  "--"}
                                              </a>
                                            ) : (
                                              "--"
                                            )}
                                          </TableCell>
                                        ) : (
                                          <TableCell key={`${i}-${index}`}>
                                            {eachList[eachHead.key] ?? "--"}
                                          </TableCell>
                                        )}
                                      </>
                                    ) : null,
                                  )}
                                  <TableCell>
                                    <button
                                      onClick={() => {
                                        toggleRow(eachList?.userid);
                                      }}
                                      className={`flex items-center justify-between gap-2 px-3 py-1.5 bg-white hover:bg-white/50 border border-gray-300 rounded-md text-sm font-medium text-gray-700 transition-all duration-300`}
                                    >
                                      <span className="flex items-start gap-1">
                                        Active Sessions
                                      </span>
                                      <div
                                        className={`${openRow == eachList?.userid ? "rotate-180" : ""} transition-transform duration-300`}
                                      >
                                        <ChevronDownIcon className="h-4.5 w-4.5 text-gray-500" />
                                      </div>
                                    </button>
                                  </TableCell>
                                </TableRow>

                                {/* Drill Table */}
                                {openRow == eachList?.userid ? (
                                  <>
                                    {/* Active */}
                                    <TableRow
                                      key={`drill-${eachList?.userid}`}
                                      className={`${Style.drillRow} !bg-[#F7F7F7] !pr-3`}
                                    >
                                      <TableCell
                                        colSpan={9999}
                                        className={"!bg-[#F7F7F7]"}
                                      >
                                        {/* ******************************************************* */}
                                        {sessionLoading ? (
                                          <InnerLoader />
                                        ) : !sessionData?.active?.length &&
                                          !sessionData?.history?.length ? (
                                          <div className="flex justify-center items-center w-full h-full">
                                            <DataNotFound
                                              title={"Active & History"}
                                            />
                                          </div>
                                        ) : (
                                          <TableCard
                                            className="!max-h-[500px]"
                                            scrollClassName="overflow-y-auto max-h-[480px]"
                                          >
                                            <Table>
                                              {/* TABLE HEAD */}
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell header sticky>
                                                    <TableHeaderWrap>
                                                      Device Info
                                                    </TableHeaderWrap>
                                                  </TableCell>
                                                  <TableCell header>
                                                    <TableHeaderWrap>
                                                      IP Address
                                                    </TableHeaderWrap>
                                                  </TableCell>
                                                  <TableCell header>
                                                    <TableHeaderWrap>
                                                      Login
                                                    </TableHeaderWrap>
                                                  </TableCell>
                                                  <TableCell header>
                                                    <TableHeaderWrap>
                                                      Logout
                                                    </TableHeaderWrap>
                                                  </TableCell>
                                                  <TableCell header>
                                                    <TableHeaderWrap>
                                                      Location
                                                    </TableHeaderWrap>
                                                  </TableCell>
                                                  <TableCell header>
                                                    <TableHeaderWrap>
                                                      Action
                                                    </TableHeaderWrap>
                                                  </TableCell>
                                                </TableRow>
                                              </TableHead>

                                              {/* TABLE BODY */}
                                              <TableBody>
                                                {/* ACTIVE SESSIONS */}
                                                {sessionData?.active?.length >
                                                  0 && (
                                                  <>
                                                    <TableRow className="bg-gray-100 !font-bold text-center">
                                                      <TableCell
                                                        colSpan={10000}
                                                      >
                                                        <div className="flex justify-center items-center gap-1">
                                                          <ActivitySquareIcon className="w-4 h-4" />
                                                          Active Sessions
                                                        </div>
                                                      </TableCell>
                                                    </TableRow>

                                                    {sessionData.active.map(
                                                      (session, index) => (
                                                        <TableRow
                                                          key={`active-${index}`}
                                                          className={
                                                            session.is_current_device
                                                              ? Style.currentDeviceRow
                                                              : ""
                                                          }
                                                        >
                                                          <TableCell sticky>
                                                            {session?.device ||
                                                              "--"}
                                                          </TableCell>
                                                          <TableCell>
                                                            {session?.ip_address ||
                                                              "--"}
                                                          </TableCell>
                                                          <TableCell>
                                                            {session?.logged_in_at ||
                                                              "--"}
                                                          </TableCell>
                                                          <TableCell>
                                                            {session?.logged_out_at ||
                                                              "--"}
                                                          </TableCell>
                                                          <TableCell>
                                                            {session?.loaction ||
                                                              "--"}
                                                          </TableCell>
                                                          <TableCell>
                                                            {session.is_current_device ? (
                                                              <div className="text-center capitalize">
                                                                <StatusChip color="green">
                                                                  This Device
                                                                </StatusChip>
                                                              </div>
                                                            ) : (
                                                              <div className="text-center capitalize">
                                                                <button
                                                                  className={
                                                                    Style.logoutButton
                                                                  }
                                                                  onClick={() =>
                                                                    handleLogout(
                                                                      session.token_id,
                                                                    )
                                                                  }
                                                                >
                                                                  <StatusChip color="red">
                                                                    Logout
                                                                  </StatusChip>
                                                                </button>
                                                              </div>
                                                            )}
                                                          </TableCell>
                                                        </TableRow>
                                                      ),
                                                    )}
                                                  </>
                                                )}

                                                {/* HISTORY SESSIONS */}
                                                {sessionData.history?.length >
                                                  0 && (
                                                  <>
                                                    <TableRow className="bg-gray-100 !font-bold text-center">
                                                      <TableCell
                                                        colSpan={10000}
                                                      >
                                                        <div className="flex justify-center items-center gap-1">
                                                          <HistoryIcon className="w-4 h-4" />
                                                          Session History
                                                        </div>
                                                      </TableCell>
                                                    </TableRow>

                                                    {sessionData.history.map(
                                                      (session, index) => (
                                                        <TableRow
                                                          key={`history-${index}`}
                                                        >
                                                          <TableCell sticky>
                                                            {session?.device ||
                                                              "--"}
                                                          </TableCell>
                                                          <TableCell>
                                                            {session?.ip_address ||
                                                              "--"}
                                                          </TableCell>
                                                          <TableCell>
                                                            {session?.logged_in_at ||
                                                              "--"}
                                                          </TableCell>
                                                          <TableCell>
                                                            {session?.logged_out_at ||
                                                              "--"}
                                                          </TableCell>
                                                          <TableCell>
                                                            {session?.loaction ||
                                                              "--"}
                                                          </TableCell>
                                                          <TableCell>
                                                            {session.is_current_device ? (
                                                              <div className="text-center capitalize">
                                                                <StatusChip color="green">
                                                                  This Device
                                                                </StatusChip>
                                                              </div>
                                                            ) : (
                                                              <div className="text-center capitalize">
                                                                <button
                                                                  className={
                                                                    Style.logoutButton
                                                                  }
                                                                  onClick={() =>
                                                                    handleLogout(
                                                                      session.token_id,
                                                                    )
                                                                  }
                                                                >
                                                                  <StatusChip color="red">
                                                                    Logout
                                                                  </StatusChip>
                                                                </button>
                                                              </div>
                                                            )}
                                                          </TableCell>
                                                        </TableRow>
                                                      ),
                                                    )}
                                                  </>
                                                )}
                                              </TableBody>
                                            </Table>
                                          </TableCard>
                                          // <div className="!max-h-[300px] h-fit overflow-hidden flex flex-col items-start justify-start">
                                          // </div>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  </>
                                ) : null}
                              </>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableCard>
                  </>
                )}
              </div>

              {!isLoading ? (
                apiData?.length > 0 ? (
                  <div
                    className={Style.table_footer}
                    style={{ alignItems: "center" }}
                  >
                    <div className={Style.table_footer_left}>
                      {`showing 1 to ${apiData?.length} of ${
                        tablePagination?.total || apiData?.length
                      }`}
                    </div>

                    {tablePagination ? (
                      <div className={Style.empDirectory_footer}>
                        {tablePagination?.next_page_url ? (
                          <button
                            className={Style.loadMoreBtn}
                            onClick={() => {
                              if (btnLoader) return;
                              // setPageCount(pageCount + 1);
                              setBtnLoader(true);
                              // filterData({ page: pageCount + 1, btnCall: true });
                              getData({
                                page: tablePagination?.next_page_url,
                              });
                            }}
                          >
                            {btnLoader ? (
                              <InnerLoader
                                className={Style.loader}
                                textStatus={false}
                              />
                            ) : (
                              "Load More"
                            )}
                          </button>
                        ) : null}
                      </div>
                    ) : null}

                    <div
                      className={`${Style.table_footer_left} ${Style.rightText}`}
                    ></div>
                  </div>
                ) : null
              ) : (
                <div
                  className={Style.table_footer}
                  style={{ alignItems: "center" }}
                >
                  <div className={Style.table_footer_left}>
                    <Skeleton width={"50%"} height={15} />
                  </div>

                  <div className={Style.empDirectory_footer}>
                    <Skeleton width={"180px"} height={40} />
                  </div>

                  <div
                    className={`${Style.table_footer_left} ${Style.rightText}`}
                  ></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <AddAccountManager
          onClose={() => {
            setShowModal(false);
            setCurrentData({});
          }}
          onSubmit={onSuccess}
          onError={onError}
          agentData={currentData}
          teams={teams}
          users={users}
          brands={brands}
          tierList={tierList}
        />
      )}
      {showModalFilterTable && (
        <DynamicFilter
          data={filterModalData}
          onClose={() => setShowModalFilterTable(false)}
          returnData={filterReturn}
          onSave={(filters, filterName, editingFilter) => {
            getData({ filter: filters });
            setShowModalFilterTable(false);
            setFilterReturn(filters);
          }}
          saveFilter={saveFilter}
          apiUrl="users/agents-list-settings?usertype=2"
        />
      )}

      {showModalCustomizeTabs && (
        <CustomizeTabsPopup
          data={tableHeader}
          onClose={() => {
            setShowModalCustomizeTabs(false);
            const table = tableRef?.current;
            if (table) {
              table.style.zIndex = 1;
            }
          }}
          onPreview={(updatedHeaders) => {
            setTableHeader(updatedHeaders);
            const table = tableRef?.current;
            table.style.zIndex = 100;
          }}
          onPreviewClose={() => {
            const table = tableRef?.current;
            table.style.zIndex = 1;
          }}
          onSave={(saveHeaders) => {
            setTableHeader(saveHeaders);
            const table = tableRef?.current;
            if (table) {
              table.style.zIndex = 1;
            }
          }}
          apiUrl="users/agents-list-settings?usertype=2"
        />
      )}

      <Snackbar
        open={alertData?.isShow}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={alertData?.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertData?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SettingAccountManager;
